import { z } from 'zod';
import { resolveFuturesMinutePriceEvidence, futuresContractSelectionSchema } from './futuresSelection.js';
import { resolveFuturesScheduledMinute } from './futuresReference.js';
const id = z.number().int().positive().max(4294967295);
const clock = z.number().int().nonnegative().max(18446744013);
const raw = z
    .string()
    .regex(/^-?(0|[1-9]\d{0,18})$/)
    .refine((v) => v === BigInt(v).toString() && BigInt(v) >= -9223372036854775808n && BigInt(v) < 9223372036854775807n);
const volume = z
    .string()
    .regex(/^(0|[1-9]\d{0,19})$/)
    .refine((v) => BigInt(v) <= 18446744073709551615n);
export const futuresSignalSeriesInputSchema = z
    .object({
    productId: id,
    code: z.string().regex(/^[a-z0-9][a-z0-9-]{0,79}$/),
    rollBufferSeconds: z.number().int().nonnegative().max(4294967295),
    overlapLookbackSeconds: z.number().int().positive().max(86400).multipleOf(60),
    adjustmentPolicy: z.enum(['completedOverlapAdditiveV1', 'completedOverlapRatioV1'])
})
    .strict();
export const futuresSignalSeriesSchema = futuresSignalSeriesInputSchema.extend({ id });
export const futuresSignalBarSchema = z
    .object({
    seriesId: id,
    productId: id,
    timestamp: clock,
    availableTimestamp: clock,
    contractId: id,
    openRaw: raw,
    highRaw: raw,
    lowRaw: raw,
    closeRaw: raw,
    volume,
    barArtifactId: id,
    definitionId: id,
    termsId: id,
    sessionDayId: id,
    lastTradeId: id,
    tradeDate: z.iso.date(),
    statusQuality: z.enum(['stableTrading', 'transition', 'unknown'])
})
    .strict()
    .refine((v) => v.timestamp % 60 === 0 &&
    v.availableTimestamp === v.timestamp + 60 &&
    BigInt(v.lowRaw) <= BigInt(v.openRaw) &&
    BigInt(v.lowRaw) <= BigInt(v.closeRaw) &&
    BigInt(v.highRaw) >= BigInt(v.openRaw) &&
    BigInt(v.highRaw) >= BigInt(v.closeRaw));
export const futuresSignalRollSchema = z
    .object({
    seriesId: id,
    productId: id,
    rollTimestamp: clock,
    availableTimestamp: clock,
    fromContractId: id,
    toContractId: id,
    overlapTimestamp: clock,
    fromCloseRaw: raw,
    toCloseRaw: raw,
    differenceRaw: raw,
    fromBarArtifactId: id,
    toBarArtifactId: id,
    fromDefinitionId: id,
    toDefinitionId: id,
    fromLastTradeId: id,
    toLastTradeId: id,
    termsId: id,
    sessionDayId: id
})
    .strict()
    .refine((v) => v.rollTimestamp % 60 === 0 &&
    v.overlapTimestamp % 60 === 0 &&
    v.availableTimestamp === v.rollTimestamp &&
    v.overlapTimestamp + 60 <= v.rollTimestamp &&
    v.fromContractId !== v.toContractId &&
    BigInt(v.toCloseRaw) - BigInt(v.fromCloseRaw) === BigInt(v.differenceRaw));
export const futuresSignalEmptyIntervalSchema = z
    .object({
    seriesId: id,
    productId: id,
    contractId: id,
    sessionDayId: id,
    startTimestamp: clock.refine((v) => v % 60 === 0),
    endTimestamp: clock.refine((v) => v % 60 === 0),
    reason: z.enum(['noTrades', 'notTrading', 'statusTransition', 'statusUnknown'])
})
    .strict()
    .refine((v) => v.endTimestamp > v.startTimestamp && v.endTimestamp - v.startTimestamp <= 86400);
export const futuresSignalDaySchema = z
    .object({
    seriesId: id,
    productId: id,
    startTimestamp: clock,
    endTimestamp: clock,
    verifiedThroughTimestamp: clock,
    openingContractId: id,
    closingContractId: id,
    candidateContractIds: z.array(id).min(1).max(31),
    sourceArtifactIds: z.array(id).min(1).max(20000),
    closedMinutes: z.number().int().nonnegative().max(1440),
    closedSourceBars: z.number().int().nonnegative().max(1440),
    missingPriceMinutes: z.number().int().nonnegative().max(1440),
    bars: z.array(futuresSignalBarSchema).max(1440),
    rolls: z.array(futuresSignalRollSchema).max(32),
    emptyIntervals: z.array(futuresSignalEmptyIntervalSchema).max(1440)
})
    .strict()
    .superRefine((v, ctx) => {
    const fail = (message) => ctx.addIssue({ code: 'custom', message });
    let previousEnd = v.startTimestamp;
    for (const e of v.emptyIntervals) {
        const selected = v.rolls
            .filter((r) => r.rollTimestamp <= e.startTimestamp)
            .sort((a, b) => a.rollTimestamp - b.rollTimestamp)
            .at(-1)?.toContractId ?? v.openingContractId;
        if (e.seriesId !== v.seriesId ||
            e.productId !== v.productId ||
            e.contractId !== selected ||
            e.startTimestamp < previousEnd ||
            e.endTimestamp > v.verifiedThroughTimestamp ||
            v.rolls.some((r) => r.rollTimestamp > e.startTimestamp && r.rollTimestamp < e.endTimestamp) ||
            v.bars.some((b) => b.timestamp >= e.startTimestamp && b.timestamp < e.endTimestamp))
            fail('Invalid futures empty interval ownership or overlap');
        previousEnd = e.endTimestamp;
    }
    if (v.emptyIntervals.reduce((sum, e) => sum + (e.endTimestamp - e.startTimestamp) / 60, 0) > v.missingPriceMinutes)
        fail('Futures empty intervals exceed absent minutes');
    if (v.startTimestamp < 86400 ||
        v.startTimestamp % 86400 !== 0 ||
        v.endTimestamp !== v.startTimestamp + 86400 ||
        v.verifiedThroughTimestamp < v.startTimestamp ||
        v.verifiedThroughTimestamp > v.endTimestamp ||
        v.verifiedThroughTimestamp % 60 !== 0 ||
        v.bars.length + v.closedMinutes + v.missingPriceMinutes !== (v.verifiedThroughTimestamp - v.startTimestamp) / 60 ||
        v.closedSourceBars > v.closedMinutes)
        fail('Invalid futures signal day/counts');
    const candidates = new Set(v.candidateContractIds), artifacts = new Set(v.sourceArtifactIds);
    if (candidates.size !== v.candidateContractIds.length ||
        artifacts.size !== v.sourceArtifactIds.length ||
        !candidates.has(v.openingContractId) ||
        !candidates.has(v.closingContractId))
        fail('Invalid futures signal day inputs');
    if (new Set(v.bars.map((b) => b.timestamp)).size !== v.bars.length ||
        new Set(v.rolls.map((r) => r.rollTimestamp)).size !== v.rolls.length)
        fail('Repeated futures signal observation');
    const rolls = [...v.rolls].sort((a, b) => a.rollTimestamp - b.rollTimestamp);
    let contractId = v.openingContractId;
    for (const roll of rolls) {
        if (roll.seriesId !== v.seriesId ||
            roll.productId !== v.productId ||
            roll.rollTimestamp < v.startTimestamp ||
            roll.rollTimestamp >= v.endTimestamp ||
            roll.rollTimestamp > v.verifiedThroughTimestamp ||
            roll.fromContractId !== contractId ||
            !candidates.has(roll.toContractId) ||
            !artifacts.has(roll.fromBarArtifactId) ||
            !artifacts.has(roll.toBarArtifactId))
            fail('Invalid futures signal roll sequence/inputs');
        contractId = roll.toContractId;
    }
    if (contractId !== v.closingContractId)
        fail('Invalid futures signal closing contract');
    for (const bar of v.bars) {
        const latest = rolls.filter((r) => r.rollTimestamp <= bar.timestamp).at(-1);
        if (bar.seriesId !== v.seriesId ||
            bar.productId !== v.productId ||
            bar.timestamp < v.startTimestamp ||
            bar.timestamp >= v.endTimestamp ||
            bar.availableTimestamp > v.verifiedThroughTimestamp ||
            bar.contractId !== (latest?.toContractId ?? v.openingContractId) ||
            !artifacts.has(bar.barArtifactId))
            fail('Invalid futures signal bar sequence/inputs');
    }
});
/** Observed signal data can retain transition/unknown status. That never grants execution admission. */
export function resolveFuturesSignalBar(input) {
    const series = futuresSignalSeriesSchema.parse(input.series);
    if (series.productId !== input.selection.productId)
        throw new Error('Futures signal series ownership mismatch');
    const evidence = resolveFuturesMinutePriceEvidence(input);
    if (evidence.state === 'unavailable') {
        if (evidence.reason === 'scheduledClosed' || evidence.reason === 'priceMissing' || evidence.reason === 'notTrading')
            return { state: 'absent', reason: evidence.reason };
        if (evidence.reason !== 'statusTransition' && evidence.reason !== 'statusUnknown')
            throw new Error(`Futures signal evidence unavailable: ${evidence.reason}`);
    }
    const source = input.bars[0];
    const schedule = resolveFuturesScheduledMinute(input.sessions, series.productId, input.selection.atTimestamp);
    if (!source || schedule.state !== 'scheduledOpen')
        throw new Error('Missing validated futures signal source');
    return {
        state: 'observed',
        bar: futuresSignalBarSchema.parse({
            seriesId: series.id,
            productId: series.productId,
            timestamp: input.selection.atTimestamp,
            availableTimestamp: input.selection.atTimestamp + 60,
            contractId: input.selection.contractId,
            openRaw: source.openRaw,
            highRaw: source.highRaw,
            lowRaw: source.lowRaw,
            closeRaw: source.closeRaw,
            volume: source.volume,
            barArtifactId: source.artifactId,
            definitionId: input.selection.definitionId,
            termsId: input.terms.id,
            sessionDayId: schedule.sessionDayId,
            lastTradeId: input.selection.lastTradeId,
            tradeDate: schedule.tradeDate,
            statusQuality: evidence.state === 'usableForMinuteModel'
                ? 'stableTrading'
                : evidence.reason === 'statusTransition'
                    ? 'transition'
                    : 'unknown'
        })
    };
}
/** Caller must provide the latest supported common minute, with both closes already known at the roll. */
export function createFuturesSignalRoll(series, rollTimestamp, overlap) {
    const policy = futuresSignalSeriesSchema.parse(series);
    clock.parse(rollTimestamp);
    const from = futuresSignalBarSchema.parse(overlap.from), to = futuresSignalBarSchema.parse(overlap.to);
    const fromSelection = futuresContractSelectionSchema.parse(overlap.fromSelection), toSelection = futuresContractSelectionSchema.parse(overlap.toSelection);
    for (const [bar, selection] of [
        [from, fromSelection],
        [to, toSelection]
    ]) {
        if (bar.seriesId !== policy.id ||
            bar.productId !== policy.productId ||
            bar.statusQuality !== 'stableTrading' ||
            bar.availableTimestamp > rollTimestamp ||
            bar.timestamp < rollTimestamp - policy.overlapLookbackSeconds ||
            selection.productId !== bar.productId ||
            selection.contractId !== bar.contractId ||
            selection.atTimestamp !== bar.timestamp ||
            selection.definitionId !== bar.definitionId ||
            selection.lastTradeId !== bar.lastTradeId)
            throw new Error('Futures roll overlap lacks causal stable evidence');
    }
    if (from.timestamp !== to.timestamp || from.termsId !== to.termsId || from.sessionDayId !== to.sessionDayId)
        throw new Error('Futures roll overlap clocks/references differ');
    if (policy.adjustmentPolicy === 'completedOverlapRatioV1' && (BigInt(from.closeRaw) <= 0n || BigInt(to.closeRaw) <= 0n))
        throw new Error('Ratio futures overlap prices must be positive');
    return futuresSignalRollSchema.parse({
        seriesId: policy.id,
        productId: policy.productId,
        rollTimestamp,
        availableTimestamp: rollTimestamp,
        fromContractId: from.contractId,
        toContractId: to.contractId,
        overlapTimestamp: from.timestamp,
        fromCloseRaw: from.closeRaw,
        toCloseRaw: to.closeRaw,
        differenceRaw: (BigInt(to.closeRaw) - BigInt(from.closeRaw)).toString(),
        fromBarArtifactId: from.barArtifactId,
        toBarArtifactId: to.barArtifactId,
        fromDefinitionId: from.definitionId,
        toDefinitionId: to.definitionId,
        fromLastTradeId: from.lastTradeId,
        toLastTradeId: to.lastTradeId,
        termsId: from.termsId,
        sessionDayId: from.sessionDayId
    });
}
/** Adjust only on the explicitly selected immutable policy. Research uses the
 * same binary64 quote-unit arithmetic as Rust indicators; raw evidence is exact.
 * The reverse multiplication order matches Rust's suffix-factor index. */
export function adjustFuturesSignalBarForAsOf(sourceInput, rollInputs, asOfTimestamp, policy) {
    const source = futuresSignalBarSchema.parse(sourceInput);
    clock.parse(asOfTimestamp);
    const adjustmentPolicy = futuresSignalSeriesInputSchema.shape.adjustmentPolicy.parse(policy);
    if (asOfTimestamp < source.availableTimestamp || rollInputs.length > 4096)
        throw new Error('Unavailable futures signal decision clock');
    const rolls = rollInputs.map((v) => futuresSignalRollSchema.parse(v));
    if (rolls.some((r) => r.seriesId !== source.seriesId || r.productId !== source.productId) ||
        new Set(rolls.map((r) => r.rollTimestamp)).size !== rolls.length)
        throw new Error('Foreign or repeated futures roll');
    const causal = rolls.filter((r) => r.availableTimestamp <= asOfTimestamp).sort((a, b) => a.rollTimestamp - b.rollTimestamp);
    for (let i = 1; i < causal.length; i++) {
        if (causal[i - 1]?.toContractId !== causal[i]?.fromContractId)
            throw new Error('Incomplete futures roll chain');
    }
    const later = causal.filter((r) => r.rollTimestamp > source.timestamp);
    const preceding = causal.filter((r) => r.rollTimestamp <= source.timestamp).at(-1);
    if ((later[0] && later[0].fromContractId !== source.contractId) || (preceding && preceding.toContractId !== source.contractId))
        throw new Error('Incomplete futures roll chain');
    let additive = 0n, factor = 1;
    if (adjustmentPolicy === 'completedOverlapRatioV1') {
        const suffix = new Array(causal.length + 1).fill(1);
        for (let i = causal.length - 1; i >= 0; i--) {
            const r = causal[i], next = suffix[i + 1];
            if (!r || next === undefined)
                throw new Error('Missing futures ratio suffix');
            if (BigInt(r.fromCloseRaw) <= 0n || BigInt(r.toCloseRaw) <= 0n)
                throw new Error('Ratio futures overlap prices must be positive');
            const value = (Number(r.toCloseRaw) / Number(r.fromCloseRaw)) * next;
            if (!Number.isFinite(value) || value <= 0)
                throw new Error('Invalid futures ratio factor');
            suffix[i] = value;
        }
        const value = suffix[causal.length - later.length];
        if (value === undefined)
            throw new Error('Missing futures ratio factor');
        factor = value;
    }
    else {
        for (const r of later)
            additive += BigInt(r.differenceRaw);
    }
    const price = (value) => {
        const result = adjustmentPolicy === 'completedOverlapRatioV1'
            ? (Number(value) / 1e9) * factor
            : Number(raw.parse((BigInt(value) + additive).toString())) / 1e9;
        if (!Number.isFinite(result) || (adjustmentPolicy === 'completedOverlapRatioV1' && BigInt(value) !== 0n && result === 0))
            throw new Error('Invalid adjusted futures research price');
        return result;
    };
    return {
        source,
        asOfTimestamp,
        open: price(source.openRaw),
        high: price(source.highRaw),
        low: price(source.lowRaw),
        close: price(source.closeRaw)
    };
}
//# sourceMappingURL=futuresSignal.js.map